/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Oauth Authentication Related Functions
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// Example values (replace with your actual OAuth URLs):
// const authUrl = 'YOUR_OAUTH_AUTHORIZATION_URL'
// const redirectUri = 'YOUR_REDIRECT_URI'
// performOAuthFlow(authUrl, redirectUri)

interface OAuthCodeResponse {
  code: string | null
  state?: string | null
  scope: string | null | undefined
  error: any
}

async function authenticateOAuthReturnCode(authUrl: string, redirectUri: string | null = null): Promise<OAuthCodeResponse> {
  return new Promise<OAuthCodeResponse>((resolve, reject) => {
    const authWindow = window.open(authUrl, '_blank', 'width=600,height=700')

    if (!authWindow) {
      reject(new Error('Failed to open authentication window. Please allow pop-ups.'))

      return
    }

    const checkWindowClosed = setInterval(() => {
      if (authWindow.closed) {
        clearInterval(checkWindowClosed)
        reject(new Error('Authentication window closed prematurely.'))
      }
    }, 500)

    const receiveMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        console.error('Received message from invalid origin:', event.origin)

        return
      }

      if (event.data && event.data.type === 'oauth_code') {
        clearInterval(checkWindowClosed)
        window.removeEventListener('message', receiveMessage)
        console.log('Received OAuth code:', event.data.code)
        authWindow.close()
        resolve(event.data.code)
      }
      else if (event.data && event.data.type === 'oauth_error') {
        clearInterval(checkWindowClosed)
        window.removeEventListener('message', receiveMessage)
        console.error('Received OAuth error:', event.data.error)
        authWindow.close()
        reject(new Error(event.data.error))
      }
    }

    window.addEventListener('message', receiveMessage)

    const pollWindow = setInterval(() => {
      try {
        if (authWindow && authWindow.location.href.startsWith(redirectUri as any)) {
          clearInterval(pollWindow)
          clearInterval(checkWindowClosed)
          window.removeEventListener('message', receiveMessage)

          const urlParams = new URLSearchParams(authWindow.location.search)
          const code: string | null = urlParams.get('code')
          const state: string | null = urlParams.get('state')
          const scope: string | null | undefined = urlParams.get('scope')
          let error = urlParams.get('error')
          authWindow.close()

          if (!code && !error) {
            error = 'OAuth authentication failed. No code or error received.'
            authWindow.close()
          }
          resolve({ code, state, scope, error })
        }
      }
      catch (e) {
        // Catch cross-origin errors, do nothing as polling continues.
      }
      if (authWindow.closed) {
        clearInterval(pollWindow)
        clearInterval(checkWindowClosed)
        window.removeEventListener('message', receiveMessage)
        reject(new Error('Authentication window closed.'))
      }
    }, 500)
  })
}

// //////////////////////////////////////////////////////////////////
// Oauth "Code" Flow - Opens Browser Window for Authentication
// Returns "code" or "error" from the OAuth Provider
// //////////////////////////////////////////////////////////////////
export async function oauthCodeFlow(authUrl: string, redirectUri: string | null = null, oauthScope: string | null = null): Promise<OAuthCodeResponse> {
  try {
    // Creates a State Object to Prevent Cross Server Attacks
    const oauthState = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

    authUrl += authUrl.includes('?') ? `&state=${oauthState}` : `?state=${oauthState}`

    // Add Response Type of "code"
    authUrl += '&response_type=code'

    // If scope was specified, add it to the URL
    if (oauthScope)
      authUrl += `&scope=${oauthScope}`

    let { code, state, scope, error } = await authenticateOAuthReturnCode(authUrl, redirectUri)

    // Validate the state to prevent cross-site request forgery
    if (state !== oauthState)
      error = 'OAuth authentication failed. Invalid state received.'

    return { code, scope, error }
  }
  catch (error) {
    console.error('OAuth authentication error:', error)

    return { code: null, state: null, scope: null, error }
  }
}

// //////////////////////////////////////////////////////////////////
// Exchange OAuth Code for Access Token - Refresh Token
// Specify the "grant_type" as "authorization_code" or "refresh_token"
// Pass "code" or "refresh_token" to "token"
// //////////////////////////////////////////////////////////////////
// export async function oauthTokenFlow(tokenUrl: string, grant_type: 'authorization_code' | 'refresh_token', token: string, clientId: string, clientSecret: string, redirectUri: string) {
//   const body = {
//     grant_type: grant_type,
//     code: token,
//     redirect_uri: redirectUri,
//   }

//   const response = await $fetch('https://lendio.us-east-1.snowflakecomputing.com/oauth/token-request', {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/x-www-form-urlencoded',
//       Authorization: `Basic ${btoa(`${clientId}:${clientSecret}`)}`,
//     },
//     body: body,
//   })

//   console.log('DONE')

// Create Headers
// const headers = new Headers()
// headers.append('Content-Type', 'application/x-www-form-urlencoded')
// headers.append('Authorization', `Basic ${btoa(`${clientId}:${clientSecret}`)}`)
// // Create Body
// const body = new URLSearchParams()
// body.append('grant_type', grant_type)
// body.append('code', token)
// body.append('redirect_uri', 'http://localhost:3000/oauth')
// Attempting To Fetch Tokens
// try {
//   const response = await fetch(tokenUrl, {
//     method: 'POST',
//     headers,
//     body,
//   })
//   if (!response.ok) {
//     throw new Error(`Failed to fetch token: ${response.statusText}`)
//   }
//   const data = await response.json()
//   return {
//     access_token: data.access_token,
//     refresh_token: data.refresh_token,
//     expires_in: data.expires_in,
//     token_type: data.token_type,
//     username: data.username,
//   }
// } catch (error: any) {
//   const error_message = `Failed to fetch token: ${error.message}`
//   console.error(error_message)
//   return { error: error_message }
// }
